import React, { useState, useMemo } from 'react'
import { Link, Redirect } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons/faExternalLink'
import { faFileCsv } from '@fortawesome/pro-regular-svg-icons/faFileCsv'
import { faUpload } from '@fortawesome/pro-regular-svg-icons/faUpload'
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes'
import { faHistory } from '@fortawesome/pro-regular-svg-icons/faHistory'

import { Helmet } from 'react-helmet'
import { Field, getIn } from 'formik'
import axios from 'axios'
import { toast } from 'react-toastify'
import isString from 'lodash/isString'
import startCase from 'lodash/startCase'

import { Heading, Group, Form, Select, Checkbox, Error } from '@peracto/peracto-ui'
import { useConfig } from '@peracto/peracto-config'

import ProductImportList from './ProductImportList'
import { IMPORT_TYPES, PRODUCT_VARIANT_IMPORT_TYPES, EMPTY_CELL_OPTIONS } from '../util'

const UNINDEXABLE_TYPES = [
    'product_option',
    'product_variant',
    'product_variant_option',
    'product_variant_inventory',
    'resource',
]
const DELETEABLE_TYPES = [
    'product',
    'product_variant',
    'product_resource',
    'product_relation',
    'product_option',
    'product_variant_option',
    'product_variant_inventory',
]

const ProductImport = () => {
    const config = useConfig()
    const API_URL = config.get('api')
    const { products } = config.get('features', {})
    const [selectedFile, setSelectedFile] = useState()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showImportHistory, setShowImportHistory] = useState(false)
    const [missingProperties, setMissingProperties] = useState()
    const [unrecognisedColumns, setUnrecognisedColumns] = useState()
    const [redirect, setRedirect] = useState()

    const importTypes = [...IMPORT_TYPES]

    if (products?.productVariants) {
        importTypes.push(...PRODUCT_VARIANT_IMPORT_TYPES)
    }

    if (products?.bulkImportOptions?.length > 0) {
        products?.bulkImportOptions.forEach(option => {
            importTypes.push({ label: startCase(option), value: option })
            UNINDEXABLE_TYPES.push(option)
        })
    }

    const handleSubmit = async (
        { importType, file, indexProduct, emptyCellHandling, removeExistingValues },
        actions
    ) => {
        if (!file) return

        setIsSubmitting(true)
        setMissingProperties()
        setUnrecognisedColumns()

        const reader = new FileReader()

        reader.readAsDataURL(file)

        const formData = new FormData()
        formData.append('file', file)
        formData.append('importType', importType)
        formData.append('sourceType', 'csv')
        if (importType === 'product_attribute') {
            formData.append(`options[${emptyCellHandling}]`, 'true')
        }

        const headers = {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
        }

        if (indexProduct && !UNINDEXABLE_TYPES.includes(importType)) {
            headers['peracto-index-product'] = true
        }

        if (removeExistingValues && DELETEABLE_TYPES.includes(importType)) {
            formData.append(`options[removeExistingValues]`, 'true')
        }

        try {
            const response = await axios.post(`${API_URL}/imports`, formData, {
                headers,
            })

            if (response?.data?.errors?.length > 0) {
                console.error('error:', response)
                const errorMessages = response?.data?.errors.map(err => err.message)
                toast.error(errorMessages.join('\n'))

                const invalidColumnData = response.data.errors.find(
                    err => err.type === 'invalid_columns'
                )

                if (invalidColumnData?.extra_data?.missing_columns?.length > 0) {
                    setMissingProperties(invalidColumnData.extra_data.missing_columns.join(', '))
                }

                if (invalidColumnData?.extra_data?.unrecognised_columns?.length > 0) {
                    setUnrecognisedColumns(
                        invalidColumnData.extra_data.unrecognised_columns.join(', ')
                    )
                }
            } else {
                console.log('success:', response)
                toast.success('Product data successfully uploaded, the import will run shortly.')
                setRedirect(`/products/import/${response.data.id}`)
            }
        } catch (e) {
            const { response } = e
            console.error('error:', response)
            if (response?.data?.violations?.length > 0) {
                // Display errors for invalid fields
                response.data.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            }
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            {redirect && <Redirect to={redirect} />}
            <Helmet>
                <title>Bulk Manage Data | Peracto</title>
            </Helmet>
            <div className="form-container">
                <Heading name="Bulk Manage Data">
                    <div className="!flex !items-center !ml-2">
                        <Link className="btn btn-outline-primary" to="/products">
                            <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                            Back to Products
                        </Link>
                    </div>
                </Heading>

                <Group key="import-data" id="import-data" name="Data Upload">
                    <Form
                        values={{
                            importType: 'product',
                            file: '',
                            emptyCellHandling: 'ignoreEmptyCells',
                            indexProduct: true,
                            removeExistingValues: false,
                        }}
                        showActionPanel={false}
                        onSubmit={handleSubmit}
                    >
                        <div className="row">
                            <div className="col-12">
                                <ol className="!py-2">
                                    <li>
                                        <label htmlFor="importType" className="!mb-2">
                                            Data to be managed in bulk:
                                        </label>
                                        <Select
                                            id="importType"
                                            name="importType"
                                            options={importTypes}
                                            className="!mb-0"
                                        />

                                        <a
                                            className="btn btn-link !px-0 !my-2"
                                            href="https://9xbagency.atlassian.net/wiki/spaces/PER/pages/243367938/Importing+and+Bulk+Managing+Product+Data"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Learn more about Bulk Management{' '}
                                            <FontAwesomeIcon
                                                icon={faExternalLink}
                                                className="!mr-2"
                                            />
                                        </a>
                                    </li>

                                    <Field name="emptyCellHandling">
                                        {({ field, form }) => (
                                            <>
                                                {form?.values?.importType ===
                                                    'product_attribute' && (
                                                    <li>
                                                        <label
                                                            htmlFor={field.name}
                                                            className="!mb-2"
                                                        >
                                                            How should empty cells be treated?
                                                        </label>
                                                        <Select
                                                            id={field.name}
                                                            name={field.name}
                                                            options={EMPTY_CELL_OPTIONS}
                                                        />
                                                    </li>
                                                )}
                                            </>
                                        )}
                                    </Field>

                                    <Field name="removeExistingValues">
                                        {({ field, form }) => (
                                            <>
                                                {DELETEABLE_TYPES.includes(
                                                    form?.values?.importType
                                                ) && (
                                                    <li>
                                                        <div className="!mt-2">
                                                            <label>
                                                                How should the data be treated?
                                                            </label>
                                                            <Checkbox
                                                                name={field.name}
                                                                label="Delete provided data"
                                                                className="!mb-0"
                                                            />
                                                        </div>

                                                        {!!field.value && (
                                                            <div className="alert alert-danger">
                                                                <span className="!font-bold">
                                                                    Warning:
                                                                </span>{' '}
                                                                Data deletion is permanent and
                                                                cannot be reversed. It is highly
                                                                recommended that you back up your
                                                                data before proceeding.
                                                            </div>
                                                        )}
                                                    </li>
                                                )}
                                            </>
                                        )}
                                    </Field>

                                    <li>
                                        <label htmlFor="importType" className="!mb-2">
                                            Which CSV file are you uploading?
                                        </label>

                                        <Field name="file">
                                            {({ field, form }) => {
                                                let error = getIn(form.errors, field.name)

                                                return (
                                                    <div className="!flex !flex-col !items-start !mb-4">
                                                        <div className="!relative !overflow-hidden">
                                                            <input
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    bottom: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    opacity: 0,
                                                                    cursor: 'pointer',
                                                                }}
                                                                name={field.name}
                                                                required
                                                                onChange={e => {
                                                                    if (e?.currentTarget?.files) {
                                                                        setSelectedFile(
                                                                            e.currentTarget.files[0]
                                                                                .name
                                                                        )
                                                                        form.setFieldValue(
                                                                            field.name,
                                                                            e.currentTarget.files[0]
                                                                        )
                                                                    }
                                                                }}
                                                                type="file"
                                                                id="fileUpload"
                                                                accept=".csv"
                                                            />
                                                            <div className="!flex !items-center">
                                                                <button className="btn btn-outline-primary !mr-2">
                                                                    <FontAwesomeIcon
                                                                        icon={faFileCsv}
                                                                        className="!mr-2"
                                                                    />
                                                                    Choose File
                                                                </button>

                                                                <span>
                                                                    {selectedFile ||
                                                                        'No File Selected'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {error && isString(error) && (
                                                            <div className="!mb-2">
                                                                <Error
                                                                    error={error}
                                                                    name={field.name}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            }}
                                        </Field>
                                    </li>

                                    <Field name="indexProduct">
                                        {({ field, form }) => (
                                            <>
                                                {!UNINDEXABLE_TYPES.includes(
                                                    form?.values?.importType
                                                ) && (
                                                    <li>
                                                        <div className="!mt-2">
                                                            <label>
                                                                Add to the Algolia product index?
                                                            </label>
                                                            <Checkbox
                                                                name={field.name}
                                                                label="Index product data"
                                                                className="!mb-0"
                                                            />
                                                        </div>
                                                    </li>
                                                )}
                                            </>
                                        )}
                                    </Field>

                                    <li>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={isSubmitting}
                                        >
                                            <FontAwesomeIcon icon={faUpload} className="!mr-2" />
                                            Upload File
                                        </button>

                                        {missingProperties && (
                                            <p className="!text-danger !mt-2">
                                                Missing properties: {missingProperties}
                                            </p>
                                        )}

                                        {unrecognisedColumns && (
                                            <p className="!text-danger !mt-2">
                                                Unrecognised columns: {unrecognisedColumns}
                                            </p>
                                        )}
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </Form>
                </Group>

                <div className="!flex !justify-center !mb-4">
                    <button
                        className="btn btn-link"
                        onClick={() => setShowImportHistory(!showImportHistory)}
                    >
                        <FontAwesomeIcon
                            icon={showImportHistory ? faTimes : faHistory}
                            className="!mr-2"
                        />
                        {showImportHistory ? 'Hide' : 'Show'} Bulk Management History
                    </button>
                </div>

                <ImportHistory showImportHistory={showImportHistory} />
            </div>
        </>
    )
}

const ImportHistory = ({ showImportHistory }) => {
    return useMemo(
        () => (
            <>
                {showImportHistory && (
                    <Group key="import-history" id="import-history" name="Bulk Management History">
                        <ProductImportList />
                    </Group>
                )}
            </>
        ),
        [showImportHistory]
    )
}

export default ProductImport
